/**
 * The described dashboard, as a host-neutral shape (#1188 slice 5).
 *
 * `core.describe_dashboard` returns one document holding the resolved
 * dashboard, its provenance, its effective ordered items after the caller's
 * sparse patch, and each item's availability. This module is the ONE place that
 * document is read, so the console and the portal cannot drift into two
 * interpretations of the same wire format.
 *
 * It is deliberately pure and renderer-free. It maps a governed item's `state`
 * to an `ExperienceState` and answers "what does this item need in order to
 * render", but it draws nothing and imports no React — `packages/experience` is
 * L0, and the moment this file knew about a component it would stop being
 * shareable by the host that does not use that component.
 *
 * The one judgement it does NOT make is whether an item may be shown. That was
 * decided server-side and travels in `state`; re-deriving it here would put
 * authority back in the browser through the side door.
 */

import { exactKeys, isRecord } from './documentShape.js'

/** The seven-arm union `packages/ui` StateBoundary renders. Restated
 *  structurally rather than imported, because L0 must not depend on the kit. */
export type DescribedItemState =
  | 'ready' | 'loading' | 'empty' | 'partial' | 'offline' | 'error' | 'refused'

export interface DescribedSource {
  measureKey?: string | null
  viewId?: string | null
  sourceKey?: string | null
}

export interface DashboardDrillBinding {
  sourceField: string
  targetField: string
}

export type DashboardDrillDescriptor =
  | {
      version: 1
      targetKey: string
      context: { kind: 'record'; entityType: string; sourceField: string }
    }
  | {
      version: 1
      targetKey: string
      context: { kind: 'filters'; bindings: DashboardDrillBinding[] }
    }

export interface DescribedItem {
  itemKey: string
  kind: string
  slot: number
  sizeHint: 'small' | 'medium' | 'full'
  group?: string | null
  hidden: boolean
  collapsed: boolean
  title?: string | null
  description?: string | null
  definition: Record<string, unknown>
  definitionVersion: number
  refreshIntent?: string | null
  refreshIntervalS?: number | null
  source: DescribedSource
  state: DescribedItemState
  reason?: string | null
  /** A governed KEY the host resolves through its own navigation. Never a route. */
  drill?: string | null
  /** Bound, versioned drill context. New hosts use this and fail closed without it. */
  drillDescriptor?: DashboardDrillDescriptor | null
  action?: string | null
  filterFields: unknown[]
  freshness?: { field: string; staleAfterMinutes: number | null } | null
}

export interface DescribedProvenance {
  scope: 'system' | 'solution' | 'tenant' | 'persona' | 'user'
  personaKey?: string | null
  solutionKey?: string | null
  host?: string | null
  pinned: boolean
  overridden: boolean
}

export interface DescribedDashboard {
  resolved: boolean
  host: string
  placement: string
  dashboard?: {
    id: string
    logicalKey: string
    name: string
    revision: number
    refreshIntent: string
    refreshIntervalS: number | null
  }
  provenance?: DescribedProvenance
  items?: DescribedItem[]
  unknownKeys?: { section: string; key: string }[]
}

const ITEM_STATES: readonly DescribedItemState[] =
  ['ready', 'loading', 'empty', 'partial', 'offline', 'error', 'refused']

const SIZES = ['small', 'medium', 'full'] as const

const DRILL_KEY = /^[a-z][a-z0-9]*(?:[._-][a-z0-9]+)*$/u
const FIELD_KEY = /^[a-z_][a-z0-9_]*$/u

/** Read the additive drill union closed. A malformed descriptor is no door. */
export function parseDashboardDrillDescriptor(value: unknown): DashboardDrillDescriptor | null {
  if (!isRecord(value) || !exactKeys(value, ['version', 'targetKey', 'context'])
      || value.version !== 1 || typeof value.targetKey !== 'string'
      || !DRILL_KEY.test(value.targetKey) || !isRecord(value.context)) return null

  const context = value.context
  if (context.kind === 'record') {
    if (!exactKeys(context, ['kind', 'entityType', 'sourceField'])
        || typeof context.entityType !== 'string' || !DRILL_KEY.test(context.entityType)
        || typeof context.sourceField !== 'string' || !FIELD_KEY.test(context.sourceField)) return null
    return {
      version: 1,
      targetKey: value.targetKey,
      context: { kind: 'record', entityType: context.entityType, sourceField: context.sourceField },
    }
  }

  if (context.kind !== 'filters' || !exactKeys(context, ['kind', 'bindings'])
      || !Array.isArray(context.bindings) || context.bindings.length === 0
      || context.bindings.length > 16) return null
  const bindings: DashboardDrillBinding[] = []
  const targets = new Set<string>()
  for (const binding of context.bindings) {
    if (!isRecord(binding) || !exactKeys(binding, ['sourceField', 'targetField'])
        || typeof binding.sourceField !== 'string' || !FIELD_KEY.test(binding.sourceField)
        || typeof binding.targetField !== 'string' || !FIELD_KEY.test(binding.targetField)
        || targets.has(binding.targetField)) return null
    targets.add(binding.targetField)
    bindings.push({ sourceField: binding.sourceField, targetField: binding.targetField })
  }
  return { version: 1, targetKey: value.targetKey, context: { kind: 'filters', bindings } }
}

/**
 * Read the door's document defensively.
 *
 * An unrecognised item is DROPPED rather than rendered as a guess: a host that
 * invents a default for a kind it does not know will happily paint something
 * plausible over a governed refusal. `unknownKinds` reports what was dropped so
 * a surface can say so, which is the same rule the override grammar follows —
 * unknown keys are ignored AND reported, never silently swallowed.
 */
export function parseDescribedDashboard(value: unknown): {
  dashboard: DescribedDashboard
  unknownKinds: string[]
} {
  const unknownKinds: string[] = []
  if (!isRecord(value) || value.resolved !== true) {
    return {
      dashboard: {
        resolved: false,
        host: isRecord(value) && typeof value.host === 'string' ? value.host : '',
        placement: isRecord(value) && typeof value.placement === 'string' ? value.placement : '',
      },
      unknownKinds,
    }
  }

  const raw = Array.isArray(value.items) ? value.items : []
  const items: DescribedItem[] = []
  for (const r of raw) {
    if (!isRecord(r) || typeof r.itemKey !== 'string' || typeof r.kind !== 'string') continue
    const state = ITEM_STATES.includes(r.state as DescribedItemState)
      ? (r.state as DescribedItemState)
      : 'error'
    const sizeHint = SIZES.includes(r.sizeHint as (typeof SIZES)[number])
      ? (r.sizeHint as (typeof SIZES)[number])
      : 'medium'
    items.push({
      itemKey: r.itemKey,
      kind: r.kind,
      slot: typeof r.slot === 'number' ? r.slot : items.length + 1,
      sizeHint,
      group: typeof r.group === 'string' ? r.group : null,
      hidden: r.hidden === true,
      collapsed: r.collapsed === true,
      title: typeof r.title === 'string' ? r.title : null,
      description: typeof r.description === 'string' ? r.description : null,
      definition: isRecord(r.definition) ? r.definition : {},
      definitionVersion: typeof r.definitionVersion === 'number' ? r.definitionVersion : 1,
      refreshIntent: typeof r.refreshIntent === 'string' ? r.refreshIntent : null,
      refreshIntervalS: typeof r.refreshIntervalS === 'number' ? r.refreshIntervalS : null,
      source: isRecord(r.source) ? (r.source as DescribedSource) : {},
      state,
      reason: typeof r.reason === 'string' ? r.reason : null,
      drill: typeof r.drill === 'string' ? r.drill : null,
      drillDescriptor: parseDashboardDrillDescriptor(r.drillDescriptor),
      action: typeof r.action === 'string' ? r.action : null,
      filterFields: Array.isArray(r.filterFields) ? r.filterFields : [],
      freshness: isRecord(r.freshness) && typeof r.freshness.field === 'string'
        ? {
            field: r.freshness.field,
            staleAfterMinutes: typeof r.freshness.staleAfterMinutes === 'number'
              ? r.freshness.staleAfterMinutes
              : null,
          }
        : null,
    })
  }

  // Items arrive ordered by the door; sorting again by the server's own slot is
  // belt and braces against a transport that does not preserve array order.
  items.sort((a, b) => a.slot - b.slot)

  return {
    dashboard: {
      resolved: true,
      host: typeof value.host === 'string' ? value.host : '',
      placement: typeof value.placement === 'string' ? value.placement : '',
      dashboard: isRecord(value.dashboard) ? (value.dashboard as DescribedDashboard['dashboard']) : undefined,
      provenance: isRecord(value.provenance)
        ? (value.provenance as unknown as DescribedProvenance)
        : undefined,
      items,
      unknownKeys: Array.isArray(value.unknownKeys)
        ? (value.unknownKeys as { section: string; key: string }[])
        : [],
    },
    unknownKinds,
  }
}

/**
 * The sentence a surface shows for the rung that answered.
 *
 * Rule 44's third consequence — a preset must be visible AS a preset. Measured
 * across seventeen shipped override mechanisms on #1679, eleven have no surface
 * at all and cannot say this; the one that does shows provenance beside the
 * effective value. This is the half that makes it sayable here.
 */
export function provenanceLabel(p: DescribedProvenance | undefined): string {
  if (!p) return ''
  switch (p.scope) {
    case 'user': return 'Your dashboard'
    case 'persona': return p.personaKey ? `Set for ${p.personaKey}` : 'Set for your role'
    case 'tenant': return 'Your organisation’s default'
    case 'solution': return 'Solution default'
    case 'system': return 'Platform default'
  }
}

/** Apply one Personalize act to a layout patch, without inventing content.
 *
 *  The design's line: "reorder, hide/show, size and group collapse", and
 *  "content changes are not smuggled into that layout patch". This function can
 *  only ever produce those five keys, which is what makes that true on the
 *  client as well as at the door — `core.assert_dashboard_override_shape`
 *  refuses anything else, so a client that tried would simply be rejected.
 */
/**
 * Which renderer a described widget asks for (#1188).
 *
 * `core.widget_kind` is the vocabulary and it is DATA: a kind is a row plus a
 * renderer, deliberately not a CHECK constraint. This is the other half — the
 * renderers this BUILD actually ships — and the two are different questions
 * that were being answered as one.
 *
 *   · the DOOR decides whether a deployment registers a kind at all, and
 *     refuses a disabled one in its own sentence
 *     (`2026090604:508`, `2026090606:134`). Authority is settled there.
 *   · this decides whether the code in front of you can DRAW it.
 *
 * Both refusals are honest and they are not the same refusal, so they do not
 * share a sentence. A deployment that registers `timeline` is not lying; a
 * build that cannot draw one yet is not broken; and a person who sees neither
 * explanation is the only bad outcome.
 *
 * ── the defect this closes ──────────────────────────────────────────────────
 * Measured on 7cd7326f: the one host that renders a dashboard
 * (`apps/portal/src/pages/AtAGlance.tsx`) NEVER reads `item.kind`. Every widget
 * — chart, list, timeline — is drawn as the same KPI paragraph. The finite
 * grammar was decorative at the only place it decides anything.
 */
export const DASHBOARD_RENDERERS = ['kpi', 'list', 'chart'] as const
export type DashboardRenderer = typeof DASHBOARD_RENDERERS[number]

export type RendererChoice =
  | { renderer: DashboardRenderer }
  /**
   * No renderer in THIS build. Carries a sentence a person can act on, because
   * a widget that draws nothing and says nothing is indistinguishable from one
   * whose data is empty.
   */
  | { renderer: null; reason: string }

/**
 * Resolve a described item's kind to a renderer.
 *
 * Unknown and not-yet-drawn are answered the same way on purpose: from the
 * reader's side they are the same situation — this build will not show it —
 * and pretending to distinguish them would mean guessing which kinds a future
 * deployment might register.
 */
export function rendererFor(kind: string): RendererChoice {
  const normalised = kind.trim().toLowerCase()
  if ((DASHBOARD_RENDERERS as readonly string[]).includes(normalised)) {
    return { renderer: normalised as DashboardRenderer }
  }
  return {
    renderer: null,
    reason: normalised
      ? `This build has no renderer for a ${normalised} item yet, so it is not drawn rather than drawn wrongly.`
      : 'This item does not say what kind it is, so it is not drawn.',
  }
}

export interface LayoutPatch {
  order?: string[]
  hidden?: string[]
  size?: Record<string, 'small' | 'medium' | 'full'>
  collapsed?: string[]
  params?: Record<string, unknown>
}

export function moveItem(
  patch: LayoutPatch, items: readonly { itemKey: string }[],
  itemKey: string, direction: -1 | 1,
): LayoutPatch {
  // The order is seeded from the CURRENT effective order, not from the stored
  // patch, so moving one tile does not silently discard the positions of items
  // the patch never named.
  const order = patch.order?.length ? [...patch.order] : items.map(i => i.itemKey)
  for (const i of items) if (!order.includes(i.itemKey)) order.push(i.itemKey)
  const from = order.indexOf(itemKey)
  if (from < 0) return patch
  const to = from + direction
  if (to < 0 || to >= order.length) return patch
  order.splice(to, 0, ...order.splice(from, 1))
  return { ...patch, order }
}

export function toggleHidden(patch: LayoutPatch, itemKey: string): LayoutPatch {
  const hidden = new Set(patch.hidden ?? [])
  if (hidden.has(itemKey)) hidden.delete(itemKey)
  else hidden.add(itemKey)
  return { ...patch, hidden: [...hidden] }
}

export function resizeItem(
  patch: LayoutPatch, itemKey: string, sizeHint: 'small' | 'medium' | 'full',
): LayoutPatch {
  return { ...patch, size: { ...(patch.size ?? {}), [itemKey]: sizeHint } }
}

export function toggleGroup(patch: LayoutPatch, groupKey: string): LayoutPatch {
  const collapsed = new Set(patch.collapsed ?? [])
  if (collapsed.has(groupKey)) collapsed.delete(groupKey)
  else collapsed.add(groupKey)
  return { ...patch, collapsed: [...collapsed] }
}
